import { useState, useCallback } from 'react';
import type { CampaignCalendar, CalendarMonth } from '@roll1/shared';
import { LIMITS } from '@roll1/shared';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CALENDAR_PRESETS, localizeCalendar } from '../data/calendar-presets';
import { Trash2, Plus, Sparkles, Pencil } from 'lucide-react';
import { useI18n } from '../i18n';
import CalendarAiTab from './CalendarAiTab';
import { Checkbox } from './ui/checkbox';

interface CalendarSettingsProps {
  calendar?: CampaignCalendar;
  onSetCalendar: (calendar: CampaignCalendar) => void;
  onRemoveCalendar: () => void;
  /** Whether AI features are available */
  aiAvailable?: boolean;
  /** Callback to request AI calendar generation */
  onRequestGeneration?: (worldDescription: string) => void;
  /** AI-generated calendar preview (set by parent after AI responds) */
  aiPreviewCalendar?: CampaignCalendar | null;
  /** The calendar was saved / removed: the host may close its window. */
  onDone?: () => void;
}

/**
 * The campaign calendar («⋯» → «Calendar: … · change…»): the current one,
 * the presets to switch to, an own calendar editor and the AI generation.
 * Nothing is saved until «Save».
 */
export default function CalendarSettings({
  calendar,
  onSetCalendar,
  onRemoveCalendar,
  aiAvailable = false,
  onRequestGeneration,
  aiPreviewCalendar,
  onDone,
}: CalendarSettingsProps) {
  const { t, locale } = useI18n();
  const [mode, setMode] = useState<'preset' | 'custom' | 'ai'>('preset');
  const [editing, setEditing] = useState<CampaignCalendar | null>(null);

  const startEditing = useCallback((source: CampaignCalendar) => {
    setEditing(JSON.parse(JSON.stringify(source)) as CampaignCalendar);
    setMode('custom');
  }, []);

  const handleSave = useCallback(() => {
    if (!editing) return;
    onSetCalendar(editing);
    setEditing(null);
    setMode('preset');
    onDone?.();
  }, [editing, onSetCalendar, onDone]);

  const handleRemove = useCallback(() => {
    onRemoveCalendar();
    setEditing(null);
    setMode('preset');
  }, [onRemoveCalendar]);

  const updateMonth = useCallback((index: number, changes: Partial<CalendarMonth>) => {
    if (!editing) return;
    const months = [...editing.months];
    months[index] = { ...months[index], ...changes };
    setEditing({ ...editing, months });
  }, [editing]);

  const addMonth = useCallback(() => {
    if (!editing || editing.months.length >= LIMITS.MAX_CALENDAR_MONTHS) return;
    setEditing({
      ...editing,
      months: [...editing.months, { name: t('timeline.calendar.newMonth'), days: 30 }],
    });
  }, [editing, t]);

  const removeMonth = useCallback((index: number) => {
    if (!editing) return;
    const months = editing.months.filter((_, i) => i !== index);
    setEditing({ ...editing, months });
  }, [editing]);

  const info = (c: CampaignCalendar, key: 'timeline.calendar.monthsInfo' | 'timeline.calendar.presetInfo'): string =>
    t(key).replace('{months}', String(c.months.length)).replace('{days}', String(c.daysPerWeek ?? '?'));

  return (
    <div className="space-y-4">
      <h3 className="text-ui font-medium">{t('timeline.calendar.title')}</h3>

      {/* Active calendar display */}
      {calendar && mode !== 'custom' && (
        <div className="p-3 bg-muted/50 rounded-lg space-y-2">
          <p className="text-meta text-muted-foreground">{t('timeline.calendar.current')}</p>
          <div className="flex items-center justify-between gap-2">
            <span className="text-ui font-medium">{calendar.name}</span>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => startEditing(calendar)}>
                <Pencil className="h-3 w-3 mr-1" /> {t('timeline.calendar.edit')}
              </Button>
              <Button variant="ghost" size="sm" onClick={handleRemove}>
                <Trash2 className="h-3 w-3 mr-1" /> {t('timeline.calendar.remove')}
              </Button>
            </div>
          </div>
          <p className="text-meta text-muted-foreground">
            {info(calendar, 'timeline.calendar.monthsInfo')}
            {calendar.yearLabel && ` (${calendar.yearLabel})`}
          </p>
        </div>
      )}

      {/* Preset selection: to set a calendar or to switch to another one */}
      {mode === 'preset' && (
        <div className="space-y-2">
          <p className="text-meta text-muted-foreground">{t('timeline.calendar.choosePreset')}</p>
          <div className="grid grid-cols-1 gap-2">
            {CALENDAR_PRESETS.map(base => localizeCalendar(base, locale)).map(preset => (
              <button
                key={preset.name}
                className="w-full text-left border rounded-lg p-3 hover:border-primary hover:bg-accent/50 transition-colors text-ui"
                onClick={() => startEditing(preset)}
              >
                <span className="font-medium block">{preset.name}</span>
                <span className="text-meta text-muted-foreground">{info(preset, 'timeline.calendar.presetInfo')}</span>
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => startEditing({ name: t('timeline.calendar.customName'), months: [{ name: t('timeline.calendar.customMonth'), days: 30 }] })}
          >
            {t('timeline.calendar.createOwn')}
          </Button>
          {aiAvailable && (
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-1"
              onClick={() => setMode('ai')}
            >
              <Sparkles className="h-3 w-3" /> {t('timeline.calendar.aiGenerate')}
            </Button>
          )}
        </div>
      )}

      {/* AI generation tab */}
      {mode === 'ai' && (
        <CalendarAiTab
          existingCalendar={calendar}
          onCalendarGenerated={(generated) => { onSetCalendar(generated); setMode('preset'); }}
          aiAvailable={aiAvailable}
          onRequestGeneration={onRequestGeneration}
          aiPreviewCalendar={aiPreviewCalendar}
        />
      )}

      {/* Own calendar editor (also a preset before it is saved) */}
      {editing && mode === 'custom' && (
        <div className="space-y-3">
          <div>
            <Label htmlFor="cal-name">{t('timeline.calendar.name')}</Label>
            <Input
              id="cal-name"
              value={editing.name}
              onChange={e => setEditing({ ...editing, name: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="cal-year-label">{t('timeline.calendar.yearLabel')}</Label>
            <Input
              id="cal-year-label"
              value={editing.yearLabel ?? ''}
              onChange={e => setEditing({ ...editing, yearLabel: e.target.value })}
              placeholder={t('timeline.calendar.yearLabelPlaceholder')}
            />
          </div>

          <div>
            <Label htmlFor="cal-epoch">{t('timeline.calendar.epoch')}</Label>
            <Input
              id="cal-epoch"
              value={editing.epoch ?? ''}
              onChange={e => setEditing({ ...editing, epoch: e.target.value })}
              placeholder={t('timeline.calendar.epochPlaceholder')}
            />
          </div>

          {/* Months table */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>{t('timeline.calendar.months')}</Label>
              <Button variant="ghost" size="sm" onClick={addMonth} aria-label={t('timeline.calendar.addMonth')}>
                <Plus className="h-3 w-3" />
              </Button>
            </div>
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {editing.months.map((month, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={month.name}
                    onChange={e => updateMonth(i, { name: e.target.value })}
                    className="flex-1 h-7 text-meta"
                  />
                  <Input
                    type="number"
                    value={month.days}
                    onChange={e => updateMonth(i, { days: Number(e.target.value) })}
                    className="w-16 h-7 text-meta"
                    min={1}
                    max={LIMITS.MAX_CALENDAR_MONTH_DAYS}
                  />
                  <label className="text-meta flex items-center gap-1" title={t('timeline.calendar.intercalary')}>
                    <Checkbox
                      checked={month.intercalary ?? false}
                      onCheckedChange={value => updateMonth(i, { intercalary: value === true })}
                    />
                    {t('timeline.calendar.intercalaryShort')}
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-6 p-0"
                    onClick={() => removeMonth(i)}
                    aria-label={t('timeline.calendar.removeMonth')}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => { setEditing(null); setMode('preset'); }}>
              {t('timeline.calendar.cancel')}
            </Button>
            <Button onClick={handleSave}>{t('timeline.calendar.save')}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
